import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export type FeedbackReason =
  | 'incorrect_information'
  | 'wrong_tone'
  | 'too_long'
  | 'missing_details'
  | 'irrelevant_content'
  | 'inappropriate'
  | 'other';

export type ConflictSourceType =
  | 'host_instructions'
  | 'knowledge_base_qa_pair'
  | 'property_data'
  | 'stay_data';

export interface ConflictSource {
  type: ConflictSourceType;
  detail: string;
  reference: string;
}

export interface ConflictItem {
  summary: string;
  sources: ConflictSource[];
  resolution: string;
}

export default class AIDraftModel extends Model {
  @attr('string') generatedContent!: string;
  @attr('string') status!: string;
  @attr('string') channel?: string;
  @attr('string') confidenceLevel?: 'high' | 'medium' | 'low';
  @attr() positiveFeedback?: boolean | null;
  @attr('string') feedbackReason?: FeedbackReason;
  @attr('number') charactersAdded!: number;
  @attr('number') charactersRemoved!: number;
  @attr('string') customPrompt?: string;
  @attr('string') sentContent?: string;
  @attr() actionable?: boolean | null;
  @attr('date') sentAt?: Date;
  @attr() conflictsDetected?: ConflictItem[];

  @belongsTo('booking', { inverse: null, async: false })
  booking?: ModelRegistry['booking'];

  @belongsTo('booking-request', { inverse: null, async: false })
  bookingRequest?: ModelRegistry['booking-request'];

  @belongsTo('enquiry', { inverse: null, async: false })
  enquiry?: ModelRegistry['enquiry'];

  @belongsTo('ai-draft', { inverse: null, async: false })
  parentDraft?: AIDraftModel;

  @cached
  get content(): string {
    return this.generatedContent;
  }

  @cached
  get hasConflicts(): boolean {
    return (this.conflictsDetected?.length ?? 0) > 0;
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'ai-draft': AIDraftModel;
  }
}
